import React, { Fragment } from "react";
import { TableCell, TableRow } from "@mui/material";
import {
  Grouping,
  AggregatedTradeData,
  AggregatedDepositData,
  AggregatedWithdrawalData,
} from "../types";
import { ReportTypes } from "../ReportsDashboard";

// Helper to count leaf rows for rowSpan
export function countLeafRows(node: any): number {
  if (Array.isArray(node)) {
    return node.reduce((sum, n) => sum + countLeafRows(n), 0);
  }
  if (node.isLeaf) {
    return node.children.length;
  }
  return countLeafRows(node.children);
}

const cellSx = {
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
};

const renderTradeGroupings = (item: AggregatedTradeData, i: number) => {
  return (
    <Fragment>
      <TableCell
        align="right"
        sx={cellSx}
        style={{
          color:
            item.tradingResult > 0
              ? "#4caf50"
              : item.tradingResult < 0
              ? "#f44336"
              : undefined,
        }}
      >
        {item.tradingResult < 0
          ? `-$${Math.abs(item.tradingResult).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : `$${item.tradingResult.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        {item.numberOfTrades.toLocaleString()}
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        $
        {item.tradingTurnover.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </TableCell>
      <TableCell
        align="right"
        sx={cellSx}
      >{`${item.winningTradesCount.toLocaleString()} (${item.winRate.toLocaleString(
        undefined,
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}%)`}</TableCell>
      <TableCell
        align="right"
        sx={cellSx}
      >{`${item.losingTradesCount.toLocaleString()} (${item.lossRate.toLocaleString(
        undefined,
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}%)`}</TableCell>
      <TableCell
        align="right"
        sx={cellSx}
      >{`${item.drawTradesCount.toLocaleString()} (${item.drawRate.toLocaleString(
        undefined,
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}%)`}</TableCell>
      <TableCell
        align="right"
        sx={cellSx}
      >{`${item.revertedTradesCount.toLocaleString()} (${item.revertRate.toLocaleString(
        undefined,
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}%)`}</TableCell>
    </Fragment>
  );
};

const renderDepositGroupings = (item: AggregatedDepositData, i: number) => {
  return (
    <Fragment>
      <TableCell align="right" sx={cellSx}>
        {item.numberOfDeposits.toLocaleString()}
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        $
        {item.amount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </TableCell>
    </Fragment>
  );
};

const renderWithdrawalGroupings = (
  item: AggregatedWithdrawalData,
  i: number
) => {
  return (
    <Fragment>
      <TableCell align="right" sx={cellSx}>
        {item.numberOfWithdrawals.toLocaleString()}
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        $
        {item.amount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </TableCell>
    </Fragment>
  );
};

// Helper to render the tree as table rows with rowSpan
export function renderGroupRowsWithRowSpan(
  tree: any[],
  groupings: Grouping[],
  level = 0,
  reportType: ReportTypes
): JSX.Element[] {
  const rows: JSX.Element[] = [];
  tree.forEach((node) => {
    if (node.isLeaf) {
      node.children.forEach(
        (
          item:
            | AggregatedTradeData
            | AggregatedDepositData
            | AggregatedWithdrawalData,
          i: number
        ) => {
          const keyParts = item.groupKey.split(" + ");
          const renderRows = () => {
            switch (reportType) {
              case ReportTypes.Trades:
                return renderTradeGroupings(item as AggregatedTradeData, i);
              case ReportTypes.Deposits:
                return renderDepositGroupings(item as AggregatedDepositData, i);
              case ReportTypes.Withdrawals:
                return renderWithdrawalGroupings(
                  item as AggregatedWithdrawalData,
                  i
                );
              default:
                return null;
            }
          };
          rows.push(
            <TableRow key={item.groupKey + "-" + i}>
              {/* For leaf rows, render empty cells for all groupings except the last, which gets the value */}
              {groupings.map((_, gIdx) =>
                gIdx === groupings.length - 1 ? (
                  <TableCell key={gIdx} sx={cellSx}>
                    {keyParts[gIdx]}
                  </TableCell>
                ) : (
                  <TableCell key={gIdx} sx={cellSx} />
                )
              )}
              {renderRows()}
            </TableRow>
          );
        }
      );
    } else {
      const leafCount = countLeafRows(node);
      let first = true;
      renderGroupRowsWithRowSpan(
        node.children,
        groupings,
        level + 1,
        reportType
      ).forEach((row, i) => {
        if (first) {
          // For the first row, render the group cell with rowSpan in the correct column, then the rest of the child row
          rows.push(
            <TableRow key={node.key + "-" + i}>
              {groupings.map((_, gIdx) =>
                gIdx === level ? (
                  <TableCell key={gIdx} rowSpan={leafCount} sx={cellSx}>
                    {node.key}
                  </TableCell>
                ) : null
              )}
              {React.Children.toArray(row.props.children).slice(level + 1)}
            </TableRow>
          );
          first = false;
        } else {
          // For subsequent rows, render empty cells for this group level, then the rest of the child row
          rows.push(
            <TableRow key={node.key + "-" + i + "-empty"}>
              {groupings.map((_, gIdx) =>
                gIdx === level ? (
                  <TableCell
                    key={gIdx}
                    style={{ display: "none" }}
                    sx={cellSx}
                  />
                ) : null
              )}
              {React.Children.toArray(row.props.children).slice(level + 1)}
            </TableRow>
          );
        }
      });
    }
  });
  return rows;
}
